import { Authentication } from "./auth";
import { PasswordChangeRequest, User } from "../types";
import { oktaAuthApi } from "@/shared/api/oktaAuthApi";
import { oktaForgotPassword } from "@/shared/api/userService";
import { UserClaims } from "@okta/okta-auth-js/lib/types";
import { loginUrl } from "@/shared/custodianSettings/employee/hsaBank";

const promiseOfUserClaims = () => {
 return new Promise<UserClaims> ((resolve, reject) => {
   return oktaAuthApi.getUser()
   .then(success => resolve(success))
   .catch(error => reject(error));
  });
};

const promiseOfValidateGroups = (requiredGroups: ReadonlyArray<string>, user: User) => {
  return new Promise<User> ((resolve, reject) => {
    if (requiredGroups.length === 0) {
      resolve(user);
      return;
    }
    if (user.groups.some(userGroup => requiredGroups.includes(userGroup))) {
      resolve(user);
      return;
    }

    return reject({ loginUrl, missingRequiredGroup: true });
  });
};

export class OktaAuth implements Authentication {
  mapUserAttributes (tokens: any, userClaims: UserClaims): User {
    const user: User = {
      displayName: userClaims.name,
      groups: tokens.accessToken.claims.groups,
      firstName: userClaims.given_name,
      lastName: userClaims.family_name,
      email: userClaims.email,
      username: userClaims.preferred_username
    };

    return user;
  }

  postLoginProcess (requiredGroups: ReadonlyArray<string>): Promise<User> {
    return oktaAuthApi.tokenManager.getTokens()
      .then(tokens =>
        promiseOfUserClaims().then(userClaims => ({ tokens, userClaims})))
      .then(({tokens, userClaims}) =>
        this.mapUserAttributes(tokens, userClaims))
      .then((user: User) =>
          promiseOfValidateGroups(requiredGroups, user));
  }

  getUser (): null | { username: string } {
    let user = null;
    promiseOfUserClaims()
      .then(claims => {
        user = claims.preferred_username;
      })
      .catch(() => user = null);

    return user ? { username: user } : null;
  }

  setInitialPassword: (username: string, oldPassword: string, newPassword: string) => Promise<User>;

  resetPassword (username: string): Promise<any> {
    return oktaForgotPassword(username);
  }

  changePassword: (request: PasswordChangeRequest) => Promise<void>;
  autoRecoverUser: (email: string) => Promise<any>;
  confirmPassword: (username: string, verificationCode: string, password: string) => Promise<any>;
  setAdmin (): void {
    return;
  }

  login (username: string, password: string, requiredGroups: ReadonlyArray<string>, mfa): Promise<User> {
    return this.postLoginProcess(requiredGroups);
  }

  logout (): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      oktaAuthApi.isAuthenticated()
      .then(async isAuthenticated => {
        if (isAuthenticated) {
          await oktaAuthApi.revokeAccessToken();
          oktaAuthApi.closeSession()
          .then(() => {
            resolve();
          }).catch(error => {
            reject(error);
            return;
          });
        }
      });
    });
  }

  accessToken (): Promise<string> {
    return new Promise((resolve, reject) => {
      oktaAuthApi.tokenManager.getTokens()
      .then(({ accessToken }) => {
        if (!accessToken) {
          resolve("");
          return;
        }
        resolve(accessToken.accessToken);
      })
      .catch(err => reject(err));
    });
  }

  forceRefreshSession (): Promise<User> {
    return oktaAuthApi.tokenManager.renew("accessToken")
    .then( _ => {
      return oktaAuthApi.tokenManager.getTokens()
      .then(tokens =>
         promiseOfUserClaims().then(userClaims => ({ tokens, userClaims})))
      .then(({tokens, userClaims}) =>
          this.mapUserAttributes(tokens, userClaims));
    });
  }
}
